import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import AppProviders from '@/components/AppProviders';

// Layouts
import MainLayout from '@/components/MainLayout';
import DashboardLayout from '@/components/DashboardLayout';

// Auth Pages
import SignInPage from '@/pages/auth/sign-in/SignInPage';
import SignUpPage from '@/pages/auth/sign-up/SignUpPage';
import ForgotPasswordPage from '@/pages/auth/forgot-password/page';
import ResetPasswordPage from '@/pages/auth/reset-password/page';
import VerifyEmailPage from '@/pages/auth/verify-email/page';
import ConfirmPasswordPage from '@/pages/auth/confirm-password/page';

// Root redirect component - redirects to sign-in if not authenticated, otherwise to dashboard
import { isAuthenticated } from '@/utils/localStorage';

function RootRedirect() {
	const authenticated = isAuthenticated();
	return <Navigate to={authenticated ? "/dashboard" : "/sign-in"} replace />;
}

// Public Pages
import TermsOfServicePage from '@/pages/public/TermsOfServicePage';
import PrivacyPolicyPage from '@/pages/public/PrivacyPolicyPage';

// Dashboard
import DashboardPage from '@/pages/dashboard/DashboardPage';

// Apps - Admin Routes
import UsersPage from '@/pages/apps/users/UsersPage';
import UserPage from '@/pages/apps/users/[userId]/[[...handle]]/UserPage';
import AreasPage from '@/pages/apps/areas/AreasPage';
import AreaPage from '@/pages/apps/areas/[areaId]/AreaPage';
import TransactionsPage from '@/pages/apps/transactions/TransactionsPage';

// Apps - Applicant Routes
import ApplicationsPage from '@/pages/apps/applications/ApplicationsPage';
import ApplicationPage from '@/pages/apps/applications/[applicationId]/ApplicationPage';
import AddressesPage from '@/pages/apps/addresses/AddressesPage';
import DocumentsPage from '@/pages/apps/documents/DocumentsPage';

// Apps - Billing (Applicant)
import BillingPage from '@/pages/apps/billing/BillingPage';
import BillingApp from '@/pages/apps/billing/BillingApp';
import PlansPage from '@/pages/apps/billing/plans/page';
import PaymentMethodPage from '@/pages/apps/billing/payment-method/page';
import SubscriptionsPage from '@/pages/apps/billing/subscriptions/page';

// Apps - Settings (Common)
import SettingsPage from '@/pages/apps/settings/SettingsPage';
import SettingsApp from '@/pages/apps/settings/settings/SettingsApp';
import AccountSettingsPage from '@/pages/apps/settings/settings/account/page';
import SecuritySettingsPage from '@/pages/apps/settings/settings/security/page';

// Error Pages
import Error404Page from '@/pages/error/Error404Page';

/**
 * Application routes - Only essential routes (20-25 pages)
 * Based on authRoles.ts configuration
 */
const router = createBrowserRouter([
	{
		path: '/',
		element: <AppProviders />,
		children: [
			{
				index: true,
				element: <RootRedirect />
			},
			// Public/Auth Routes
			{
				path: 'sign-in',
				element: (
					<ProtectedRoute requireAuth={false}>
						<MainLayout navbar={false} toolbar={false} footer={false}>
							<SignInPage />
						</MainLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'sign-up',
				element: (
					<ProtectedRoute requireAuth={false}>
						<MainLayout navbar={false} toolbar={false} footer={false}>
							<SignUpPage />
						</MainLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'forgot-password',
				element: (
					<ProtectedRoute requireAuth={false}>
						<MainLayout navbar={false} toolbar={false} footer={false}>
							<ForgotPasswordPage />
						</MainLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'reset-password',
				element: (
					<ProtectedRoute requireAuth={false}>
						<MainLayout navbar={false} toolbar={false} footer={false}>
							<ResetPasswordPage />
						</MainLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'verify-email',
				element: (
					<ProtectedRoute requireAuth={false}>
						<MainLayout navbar={false} toolbar={false} footer={false}>
							<VerifyEmailPage />
						</MainLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'confirm-password',
				element: (
					<ProtectedRoute requireAuth={false}>
						<MainLayout navbar={false} toolbar={false} footer={false}>
							<ConfirmPasswordPage />
						</MainLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'terms-of-service',
				element: (
					<MainLayout navbar={false} toolbar={false} footer={false}>
						<TermsOfServicePage />
					</MainLayout>
				)
			},
			{
				path: 'privacy-policy',
				element: (
					<MainLayout navbar={false} toolbar={false} footer={false}>
						<PrivacyPolicyPage />
					</MainLayout>
				)
			},
			// Protected Routes - Dashboard
			{
				path: 'dashboard',
				element: (
					<ProtectedRoute requireAuth={true}>
						<DashboardLayout>
							<DashboardPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			// Protected Routes - Admin Only
			{
				path: 'apps/users',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Admin']}>
						<DashboardLayout>
							<UsersPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/users/:userId/*',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Admin']}>
						<DashboardLayout>
							<UserPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/areas',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Admin']}>
						<DashboardLayout>
							<AreasPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/areas/:areaId/*',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Admin']}>
						<DashboardLayout>
							<AreaPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/transactions',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Admin']}>
						<DashboardLayout>
							<TransactionsPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			// Protected Routes - Applicant Only
			{
				path: 'apps/applications',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<ApplicationsPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/applications/:applicationId/*',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<ApplicationPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/addresses',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<AddressesPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/documents',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<DocumentsPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			// Protected Routes - Billing (Applicant)
			{
				path: 'apps/billing',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<BillingPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/billing/plans',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<BillingApp>
								<PlansPage />
							</BillingApp>
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/billing/payment-method',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<BillingApp>
								<PaymentMethodPage />
							</BillingApp>
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/billing/subscriptions',
				element: (
					<ProtectedRoute requireAuth={true} allowedRoles={['Applicant']}>
						<DashboardLayout>
							<BillingApp>
								<SubscriptionsPage />
							</BillingApp>
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			// Protected Routes - Settings (Common)
			{
				path: 'apps/settings',
				element: (
					<ProtectedRoute requireAuth={true}>
						<DashboardLayout>
							<SettingsPage />
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/settings/account',
				element: (
					<ProtectedRoute requireAuth={true}>
						<DashboardLayout>
							<SettingsApp>
								<AccountSettingsPage />
							</SettingsApp>
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			{
				path: 'apps/settings/security',
				element: (
					<ProtectedRoute requireAuth={true}>
						<DashboardLayout>
							<SettingsApp>
								<SecuritySettingsPage />
							</SettingsApp>
						</DashboardLayout>
					</ProtectedRoute>
				)
			},
			// 404 Page
			{
				path: '*',
				element: (
					<MainLayout navbar={false} toolbar={false} footer={false}>
						<Error404Page />
					</MainLayout>
				)
			}
		]
	}
]);

// Export the router for use in App.tsx
export { router };

